import React, { useRef, useState } from 'react';

/**
 * MfaCodeInput — the runtime login "step 2" code entry (plan §12.1).
 *
 * A single <input inputmode="numeric" autocomplete="one-time-code"> is rendered
 * as 6 visual cells. The component auto-submits on the 6th digit. A recovery
 * code toggle swaps the cells for one text input (`XXXXX-XXXXX`).
 *
 * Props:
 *   - value: string — the current code (numeric 0-9, or recovery text)
 *   - onChange(next): void
 *   - onSubmit(code): void — fired automatically when 6 digits are present
 *   - error: string | null — "That code didn't work. N attempts left."
 *   - disabled: boolean
 *   - recoveryMode / onToggleRecovery: controlled toggle state
 */
export default function MfaCodeInput({
    value,
    onChange,
    onSubmit,
    error,
    disabled = false,
    recoveryMode = false,
    onToggleRecovery,
}) {
    const inputRef = useRef(null);
    const [focused, setFocused] = useState(false);

    const isRecovery = recoveryMode;

    const handleChange = (e) => {
        const raw = e.target.value;
        // Numeric mode: strip non-digits, cap at 6. Recovery mode: allow the
        // X-XXXXX shape, uppercase, cap at 11 chars.
        const next = isRecovery
            ? raw.replace(/[^a-zA-Z0-9-]/g, '').toUpperCase().slice(0, 11)
            : raw.replace(/[^0-9]/g, '').slice(0, 6);
        onChange(next);
        if (!isRecovery && next.length === 6 && onSubmit) {
            onSubmit(next);
        }
    };

    // Render the 6 cells for the numeric mode. A single hidden input drives the
    // value; cells are purely visual and reflect the typed digits.
    const cells = isRecovery
        ? null
        : Array.from({ length: 6 }).map((_, i) => {
              const digit = value[i] || '';
              return (
                  <div
                      key={i}
                      className={`w-10 h-12 rounded-lg border flex items-center justify-center text-xl font-semibold transition-colors ${
                          digit
                              ? 'border-stone-900 text-stone-900'
                              : focused
                              ? 'border-stone-400 text-stone-300'
                              : 'border-stone-200 text-stone-300'
                      }`}
                      aria-hidden="true"
                  >
                      {digit}
                  </div>
              );
          });

    return (
        <div className="space-y-4">
            <div className="flex justify-center" onClick={() => inputRef.current?.focus()}>
                {isRecovery ? (
                    <input
                        ref={inputRef}
                        value={value}
                        onChange={handleChange}
                        disabled={disabled}
                        inputMode="text"
                        autoComplete="one-time-code"
                        placeholder="XXXXX-XXXXX"
                        data-testid="mfa-recovery-input"
                        className="w-full max-w-xs px-4 py-2.5 text-center text-sm tracking-widest font-mono border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-stone-900/10 focus:border-stone-400 transition-all"
                    />
                ) : (
                    <>
                        <input
                            ref={inputRef}
                            value={value}
                            onChange={handleChange}
                            onFocus={() => setFocused(true)}
                            onBlur={() => setFocused(false)}
                            disabled={disabled}
                            inputMode="numeric"
                            autoComplete="one-time-code"
                            autoFocus
                            aria-label="6-digit verification code"
                            data-testid="mfa-code-input"
                            className="sr-only"
                        />
                        <div className="flex gap-2">{cells}</div>
                    </>
                )}
            </div>

            {error && (
                <p data-testid="mfa-code-error" className="text-center text-sm text-red-600">
                    {error}
                </p>
            )}

            <div className="text-center">
                {isRecovery ? (
                    <button
                        type="button"
                        onClick={() => onToggleRecovery?.(false)}
                        data-testid="mfa-use-code-link"
                        className="text-sm text-stone-500 hover:text-stone-700 font-medium"
                    >
                        Use a code from your authenticator instead
                    </button>
                ) : (
                    <button
                        type="button"
                        onClick={() => onToggleRecovery?.(true)}
                        data-testid="mfa-use-recovery-link"
                        className="text-sm text-stone-500 hover:text-stone-700 font-medium"
                    >
                        Use a recovery code instead
                    </button>
                )}
            </div>
        </div>
    );
}
